import React, { PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { useTheme } from "@mui/material";
import { UnisphereProvider } from "@unisphere/runtime-react";
import { UnisphereWorkspaceConfig } from "@unisphere/runtime";
import { version } from "@kaltura/mediaspace-shared-data-kms-version";
import * as InAppMessaging from "@kaltura/unisphere-in-app-messaging-core";
import { registerElementInGlobalKalturaVersions } from "@unisphere/core";

/**
 * Render Unisphere provider, initialized with values from KMS configs
 */
export const KmsUnisphereProvider = ({ children }: PropsWithChildren) => {
    const config = useContext(ConfigContext);
    const serverUrl = config.application.unisphereWorkspaceUrl;
    const application = config.application.isKAF ? "KAF" : "KMS";
    const { partnerId, userRole, profileName } = config.application;
    const { analytics } = config;
    const { pageType = "Unknown" } = analytics;
    const inAppMessagingProps = config.inAppMessaging;

    useEffect(() => {
        registerElementInGlobalKalturaVersions({
            _schemaVersion: "1",
            type: "host",
            productName: "MediaSpace",
            subProductName: profileName,
            version,
            origin: "url",
        });
    }, [profileName]);

    const theme = useTheme();

    /*
     * z-index of DOM elements that Unisphere renders on the body tag.
     * They should be on top of all regular elements on the page, and below MUI modals (1300 by default)
     */
    const unisphereZIndex = theme.zIndex.modal - 1;

    const unisphereConfig = useMemo<UnisphereWorkspaceConfig>(() => {
        const runtimes: UnisphereWorkspaceConfig["runtimes"] = [
            {
                widgetName: "unisphere.widget.notifications",
                runtimeName: "notifications",
                settings: {},
                visuals: [
                    {
                        type: "container",
                        target: { type: "body" },
                        settings: {},
                    },
                ],
                ui: {
                    bodyContainer: {
                        zIndex: unisphereZIndex,
                    },
                },
            },
        ];

        if (inAppMessagingProps) {
            runtimes.push({
                runtimeName: InAppMessaging.EngineRuntimeName,
                widgetName: InAppMessaging.WidgetName,
                settings: {
                    hostUser: {
                        roles: [userRole],
                        partnerId: String(partnerId),
                        ks: inAppMessagingProps.ks,
                    },
                    hostApp: {
                        currentPage: pageType,
                    },
                    analytics: {
                        analyticsServerUrl: "https://" + analytics.serviceUrl,
                        hostApplicationNumber: analytics.appType,
                        hostApplicationVersion: version,
                    },
                } satisfies InAppMessaging.EngineRuntimeSettings,
                ui: {
                    bodyContainer: {
                        zIndex: unisphereZIndex,
                    },
                },
            });
        }

        return {
            serverUrl,
            appId: application,
            appProfileId: profileName,
            appVersion: version,
            runtimes,
            ui: {
                bodyContainer: {
                    zIndex: unisphereZIndex,
                },
            },
        };
    }, [
        serverUrl,
        application,
        partnerId,
        userRole,
        profileName,
        analytics,
        pageType,
        unisphereZIndex,
        inAppMessagingProps,
    ]);

    return (
        <UnisphereProvider
            initialConfiguration={unisphereConfig}
            kalturaTheme={theme.kaltura}
            language={config.application.currentLocaleCode ?? "en"}
        >
            {children}
        </UnisphereProvider>
    );
};
